import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cx from 'clsx'
import SolidExclamationCircleIcon from 'components/Luxkit/Icons/solid/SolidExclamationCircleIcon'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import { hasIncludedGuestsExceeded, getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import GeoContext from 'contexts/geoContext'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import TextButton from 'components/Luxkit/Button/TextButton'
import { sum } from 'lib/array/arrayUtils'
import { getBundleValueUp } from 'lib/bundleAndSave/getBundleValueUp'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'

const Root = styled.div`
  display: flex;
  flex-direction: column;

  &.align-left {
    align-items: flex-start;
  }

  &.align-center {
    align-items: center;
  }

  &.align-right {
    align-items: flex-end;
  }
`

export const StyledExclamationIcon = styled(SolidExclamationCircleIcon)`
  position: relative;
  margin-left: ${rem(5)};
  top: ${rem(3)};
`

interface Props {
  offer: App.BundleOffer;
  className?: string;
  rooms: Array<App.Occupants>;
  pkg: App.BundlePackageWithPrice;
  align?: 'left' | 'center' | 'right' | 'none';
  offerUrl: string;
}

function BundleOfferTilePricing(props: Props) {
  const {
    offer,
    className,
    pkg,
    align,
    rooms,
    offerUrl,
  } = props

  const { isTaxExclusiveRegion } = useContext(GeoContext)
  const priceMultiplier = rooms.length || 1
  const extraGuestSurcharges = useMemo(() => sum(pkg.packages.map(p => getSumExtraGuestsSurcharge(p, rooms, offer.bundledOffers[p.offerId]))), [pkg, rooms, offer])
  const taxesAndFees = pkg.taxesAndFees || 0
  const propertyFees = (pkg?.propertyFees ?? 0) * priceMultiplier || 0

  const hotelPrice = ((pkg.price + (pkg.surcharge ?? 0)) * priceMultiplier) + extraGuestSurcharges
  const totalPrice = (isTaxExclusiveRegion ? hotelPrice : hotelPrice + propertyFees)

  const showIncludedGuestsExceeded = useMemo(() => {
    if (pkg.packages.some((p) => !p.roomType || (p.roomRate?.extraGuestSurcharges?.length || 0) > 0)) {
      return false
    }

    return rooms.some(room => pkg.packages.every(p => hasIncludedGuestsExceeded(room, p, offer.bundledOffers[p.offerId])))
  }, [pkg, rooms, offer])

  const { shouldShowDiscountPercent, hotelDiscountPercent, showPriceDetails } = useMemo(() => getBundleValueUp(offer, pkg), [offer, pkg])

  return (
    <Root className={cx(className, `align-${align}`)}>
      {shouldShowDiscountPercent && !!hotelDiscountPercent && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={hotelDiscountPercent}
      />}
      <PriceRowPriceCaption>{pkg.durationLabel} from</PriceRowPriceCaption>
      <PriceRowPrice
        data-testid="search-price"
        size="L"
        price={totalPrice}
        saleUnit={rooms.length === 0 ? offer.saleUnit : 'total'}
        taxesAndFees={taxesAndFees}
      />
      <PriceRowTaxesAndFees
        isTaxExclusiveRegion={offer.property?.taxesPayableAtProperty}
        taxesAndFees={taxesAndFees}
      />
      {showIncludedGuestsExceeded && (
        <AdditionalGuestsPopup
          complex={false}
          description="+ Extra guest surcharge"
        />
      )}
      <PriceRowAgentHubCommission
        size="L"
        productType={offer.productType}
      />
      {showPriceDetails && <PriceDetailsModal
        trigger="price-row"
        triggerSize="M"
        offer={offer}
        duration={pkg.duration}
        taxesAndFees={taxesAndFees}
        propertyFees={propertyFees}
        hotelPrice={hotelPrice}
        rooms={rooms}
        dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
        cta={
          <TextButton kind="primary" fit="flex" to={offerUrl}>
            View Offer
          </TextButton>
        }
      />}
    </Root>
  )
}

BundleOfferTilePricing.defaultProps = {
  rooms: [],
  align: 'none',
} as Partial<Props>

export default BundleOfferTilePricing
